import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "./ui/button";
import {
  Crown,
  CalendarCheck,
  MapPin,
  ChartLineUp,
  ArrowRight,
  ArrowLeft,
  X,
  Confetti,
  GoogleLogo,
  MicrosoftOutlookLogo,
  AppleLogo,
} from "@phosphor-icons/react";

/**
 * ProUpgradeTour — one-time welcome carousel for owners on their first
 * dashboard visit after upgrading to Pro. Four short slides:
 *   1. Welcome — recap what Pro unlocks
 *   2. Calendar sync — point at the trifecta + nudge card
 *   3. Priority pin — show off the map advantage
 *   4. Insights & Pro analytics — tell them where to track impact
 *
 * Dismiss is sticky (`POST /api/owner/pro-tour/dismiss`) so the tour
 * never re-opens automatically. Closing on slide 4 fires the same
 * dismiss call so completing-the-tour ≠ keep-showing-it.
 */
const SLIDES = [
  {
    key: "welcome",
    Icon: Crown,
    title: "Welcome to TruckTrackr Pro 🎉",
    body: (name) => (
      <>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {name ? <>Hey {name} — </> : null}your account just unlocked the
          full Pro toolkit. Quick 60-second tour so you don&apos;t miss anything.
        </p>
        <ul className="mt-4 space-y-1.5 text-[13px] text-foreground/90">
          <li className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-none" />
            Sync your schedule with Google, Outlook, or Apple Calendar (two-way)
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-none" />
            Priority pin on the public map — always above free trucks
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-none" />
            Insights — see exactly who&apos;s viewing your truck and from where
          </li>
        </ul>
      </>
    ),
  },
  {
    key: "calendar",
    Icon: CalendarCheck,
    title: "Stop double-entering your schedule",
    body: () => (
      <>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Connect a calendar once — events flow in BOTH directions automatically.
          Edits in Google → public map. Edits on TruckTrackr → Google. Same for Outlook.
        </p>
        <div className="mt-4 grid grid-cols-3 gap-2 text-[11px] uppercase tracking-wider font-semibold">
          <div className="rounded-lg border border-border bg-card p-2.5 text-center">
            <GoogleLogo weight="duotone" className="mx-auto h-5 w-5 text-primary mb-1" />
            Google
            <div className="text-[10px] text-muted-foreground normal-case font-normal mt-0.5">Two-way</div>
          </div>
          <div className="rounded-lg border border-border bg-card p-2.5 text-center">
            <MicrosoftOutlookLogo weight="duotone" className="mx-auto h-5 w-5 text-primary mb-1" />
            Outlook
            <div className="text-[10px] text-muted-foreground normal-case font-normal mt-0.5">Two-way</div>
          </div>
          <div className="rounded-lg border border-border bg-card p-2.5 text-center">
            <AppleLogo weight="duotone" className="mx-auto h-5 w-5 text-primary mb-1" />
            Apple
            <div className="text-[10px] text-muted-foreground normal-case font-normal mt-0.5">Read-only feed</div>
          </div>
        </div>
        <div className="mt-3 text-[12px] text-muted-foreground italic">
          Skip the tour and you&apos;ll see a &ldquo;Sync your calendar&rdquo; card at the
          top of your dashboard with the same shortcut.
        </div>
      </>
    ),
    action: {
      label: "Connect a calendar now",
      run: () => {
        const trigger = document.querySelector('[data-testid="calendar-howto-trigger"]');
        const section = trigger?.closest("div");
        section?.scrollIntoView({ behavior: "smooth", block: "start" });
        setTimeout(() => trigger?.click?.(), 450);
      },
    },
  },
  {
    key: "pin",
    Icon: MapPin,
    title: "Your priority pin is live",
    body: () => (
      <>
        <p className="text-sm text-muted-foreground leading-relaxed">
          On the public map at <strong className="text-foreground">thetrucktrackr.com</strong>,
          your truck now renders with a larger orange Pro pin that sits ABOVE every
          free truck at the same location. Customers see you first.
        </p>
        <div className="mt-4 rounded-xl border border-border bg-card p-4 flex items-center justify-center gap-6">
          <div className="text-center">
            <div className="inline-grid place-items-center h-9 w-9 rounded-full bg-muted text-muted-foreground text-[10px] font-bold">
              FREE
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">Standard pin</div>
          </div>
          <ArrowRight weight="bold" className="h-5 w-5 text-muted-foreground" />
          <div className="text-center">
            <div className="inline-grid place-items-center h-12 w-12 rounded-full bg-primary text-primary-foreground text-[11px] font-bold shadow-lg ring-4 ring-primary/20">
              PRO
            </div>
            <div className="text-[10px] text-primary mt-1 font-semibold">Priority pin</div>
          </div>
        </div>
      </>
    ),
    action: { label: "View the live map", run: "openMap" },
  },
  {
    key: "insights",
    Icon: ChartLineUp,
    title: "Track who's noticing you",
    body: () => (
      <>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Scroll down your dashboard to see <strong className="text-foreground">Pro Insights</strong>:
          how many viewers your truck pulled this week, where they&apos;re from,
          and which posts converted to follows or push notifications.
        </p>
        <p className="mt-3 text-[13px] text-foreground/90">
          That&apos;s it — the rest is just posting your schedule. You&apos;ve got this.
        </p>
        <p className="mt-3 text-[12px] text-muted-foreground italic">
          Need to revisit this tour? Go to <em>Settings → Replay Pro tour</em>.
        </p>
      </>
    ),
    final: true,
  },
];

function runActionFromSlide(run, navigate, handleDismiss) {
  if (run === "openMap") {
    navigate("/");
  } else if (typeof run === "function") {
    run();
  }
  handleDismiss();
}

export default function ProUpgradeTour({ name, onDismiss }) {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(true);
  const slide = SLIDES[idx];
  const isLast = idx === SLIDES.length - 1;
  const { Icon } = slide;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") handleDismiss(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  const handleDismiss = async () => {
    try { await api.post("/owner/pro-tour/dismiss", {}); } catch (_) { /* ignore */ }
    setOpen(false);
    onDismiss?.();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/55 p-4"
      data-testid="pro-upgrade-tour"
      onClick={handleDismiss}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-border bg-card shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="relative bg-gradient-to-br from-primary/15 via-primary/5 to-transparent px-5 pt-5 pb-3">
          <button
            type="button"
            onClick={handleDismiss}
            className="absolute top-3 right-3 rounded-full p-1 text-muted-foreground hover:bg-muted/60 hover:text-foreground transition"
            aria-label="Close tour"
            data-testid="pro-tour-close"
          >
            <X weight="bold" className="h-4 w-4" />
          </button>
          <div className="inline-grid place-items-center h-11 w-11 rounded-2xl bg-primary/15 text-primary mb-2">
            <Icon weight="duotone" className="h-6 w-6" />
          </div>
          <h2
            className="font-display font-semibold text-xl tracking-tight text-foreground"
            data-testid="pro-tour-title"
          >
            {slide.title}
          </h2>
        </header>

        <div className="px-5 py-4" data-testid={`pro-tour-slide-${slide.key}`}>
          {slide.body(name)}
        </div>

        <footer className="flex items-center justify-between gap-2 px-5 py-3 border-t border-border bg-muted/20">
          <div className="flex items-center gap-1.5">
            {SLIDES.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-6 bg-primary" : "w-1.5 bg-muted-foreground/30"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {idx > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIdx((i) => i - 1)}
                data-testid="pro-tour-prev"
              >
                <ArrowLeft weight="bold" className="h-3.5 w-3.5 mr-1" />
                Back
              </Button>
            )}
            {slide.action && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => runActionFromSlide(slide.action.run, navigate, handleDismiss)}
                data-testid="pro-tour-action"
              >
                {slide.action.label}
              </Button>
            )}
            {isLast ? (
              <Button
                size="sm"
                onClick={handleDismiss}
                data-testid="pro-tour-finish"
                className="gap-1.5"
              >
                <Confetti weight="bold" className="h-3.5 w-3.5" />
                Got it!
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => setIdx((i) => i + 1)}
                data-testid="pro-tour-next"
              >
                Next
                <ArrowRight weight="bold" className="h-3.5 w-3.5 ml-1" />
              </Button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
